import { useNewJoinersAndAllocationsInvestorsTableController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsInvestors/NewJoinersAndAllocationsInvestorsTable/useNewJoinersAndAllocationsInvestorsTableController';
import {
	NewJoinersAndAllocationsContractDataItem,
	NewJoinersAndAllocationsInvestorLocal,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/useNewJoinersAndAllocationsModalController';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { NumberFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/NumberFormatted';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { TableSecurityTagPair } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/common';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	Cell,
	HeaderCell,
	HeaderChildrenWrapper,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { isInvalidTableTextCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/defautTableUtils';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { TableCheckbox } from '@dealstack/frontend/shared/components/Table/DefaultTable/TableComponents';
import { TableHeaderContractCellGroup } from '@dealstack/frontend/shared/components/TableHeaderContractCellGroup/TableHeaderContractCellGroup';
import { TextWithTooltip } from '@dealstack/frontend/shared/components/TextWithTooltip/TextWithTooltip';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	useTranslationCommonUi,
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController';
import {
	Button,
	Flex,
	Select,
	Stack,
	Text as TextMantine,
	Tooltip as TooltipMantine,
} from '@mantine/core';
import React, { Dispatch, SetStateAction, useMemo } from 'react';
import styled from 'styled-components';

import { useJoinerAllocationsVesting } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsInvestors/NewJoinersAndAllocationsInvestorsTable/useJoinerAllocationsVesting.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { ITextProps } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import { Align } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell.types';
import { NewJoinersAndAllocationsInvestorSideAgreementsPopup as SideAgreementsPopup } from './NewJoinersAndAllocationsInvestorSideAgreementsPopup';

interface Props {
	className?: string;
	investorsList: NewJoinersAndAllocationsInvestorLocal[];
	setInvestorsList: Dispatch<SetStateAction<NewJoinersAndAllocationsInvestorLocal[]>>;
}

const NewJoinersAndAllocationsInvestorsTable = ({
	investorsList,
	setInvestorsList,
	className,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();

	const {
		onUpdateInvestorContracts,
		sideAgreements,
		mainContracts,
		totalSideAgreementsAssigned,
		securitiesList,
		currency,
		attachToAllInvestorCheckboxStateArray: contractsAllSelection,
		loansDefinitionsList,
		hasSecuritiesSubjectToVesting,
	} = useNewJoinersAndAllocationsInvestorsTableController(investorsList, setInvestorsList);

	const {
		vestingTerms,
		vestingBulkUpdateMode,
		bulkVestingTerm,
		investorVestingTerms,
		onBulkUpdateModeChange,
		onBulkVestingTermChange,
		onInvestorCheckboxClick,
		onInvestorDropdownChange,
		selectedInvestors,
		selectAllInvestors,
		isAllInvestorsSelected,
	} = useJoinerAllocationsVesting(investorsList, setInvestorsList);
	const showVesting = hasSecuritiesSubjectToVesting;

	const onSelectAllClicked = (contractProp: NewJoinersAndAllocationsContractDataItem): void => {
		if (!investorsList) {
			return;
		}
		const isContractSelectedForEveryone =
			contractsAllSelection?.find(
				(itemContract) => itemContract.contractID === contractProp.id
			)?.isChecked ?? false;
		const updatedInvestorList = investorsList?.map((investor) => {
			const updatedContracts = isContractSelectedForEveryone
				? investor.contracts?.filter(
						(investorContractID) => investorContractID !== contractProp.id
					)
				: [...(investor.contracts || []), contractProp.id];
			return { ...investor, contracts: updatedContracts };
		});
		setInvestorsList(updatedInvestorList);
	};
	const getLoanDefinitionTypeTranslation = useSecurityTypeTranslation();

	const makeVestingTermDropdown = (
		defaultValue: string | null,
		onChange: (value: string) => void
	) => {
		const data = vestingTerms?.length
			? [
					...vestingTerms.map((vestingTerm) => ({
						label: vestingTerm.name,
						value: vestingTerm.id,
					})),
				]
			: [];
		return (
			<Select
				data={data}
				size="md"
				rightSection={<Icon variant="caret-down" size="vlg" />}
				rightSectionProps={{ style: { pointerEvents: 'none' } }}
				value={defaultValue}
				onChange={onChange}
				placeholder={tCommonUi('common_placeholders.select')}
				sx={{
					'input:focus:not([data-invalid])': {
						boxShadow: 'none',
					},
				}}
			/>
		);
	};

	return (
		<div className={className}>
			<DefaultTable maxSize>
				<HeaderRow>
					<HeaderCell
						size={2}
						sectionSeparator
						groupLabel={tMipCreate('investors.table.header.name')}
						sectionLabel={tMipCreate('investors.table.header.individual')}
						sectionIcon="person"
					>
						{tMipCreate('investors.table.header.first_name')}
					</HeaderCell>
					<HeaderCell size={2}>
						{tMipCreate('investors.table.header.last_name')}
					</HeaderCell>
					<HeaderCell size={4} groupLabel={tMipCreate('investors.table.header.contacts')}>
						{tMipCreate('investors.table.header.email')}
					</HeaderCell>

					{securitiesList?.length
						? securitiesList?.map((security, index) => {
								const isFirst = index === 0;
								return (
									<React.Fragment
										key={`${security.id}${security.name}${security.type}`}
									>
										<HeaderCell
											size={1.5}
											groupComponent={
												<TableSecurityTagPair
													size={3.2} // Combination of two cells of sizes 1.5 + 1.7.
													name={security.name}
													type={security.type}
												/>
											}
											sectionSeparator={isFirst}
											sectionLabel={
												isFirst
													? tMipCreate(
															'investors.table.header.securities'
														)
													: undefined
											}
											childrenAlign="center"
											sectionIcon="bank"
										>
											{tMipCreate('investors.table.header.number')}
										</HeaderCell>
										<HeaderCell size={1.7} childrenAlign="center">
											{tMipCreate('investors.table.header.total_cost')}
										</HeaderCell>
									</React.Fragment>
								);
							})
						: null}

					{loansDefinitionsList?.length
						? loansDefinitionsList?.map((loanDefinition, index) => {
								const isFirst = index === 0;
								const typeTranslation = getLoanDefinitionTypeTranslation(
									loanDefinition.type
								);

								return (
									<React.Fragment
										key={`${loanDefinition.id}${loanDefinition.name}${loanDefinition.type}`}
									>
										<HeaderCell
											size={1.8}
											groupComponent={
												<Stack gap={2}>
													<TooltipMantine label={typeTranslation}>
														<TextMantine
															size="B6Medium"
															c={defaultTheme.v2.color.neutral[600]}
															truncate={true}
															lineClamp={1}
															style={{ wordBreak: 'break-all' }}
														>
															{typeTranslation}
														</TextMantine>
													</TooltipMantine>
													<TooltipMantine label={loanDefinition.name}>
														<TextMantine
															size="B6Bold"
															truncate={true}
															lineClamp={1}
															style={{ wordBreak: 'break-all' }}
														>
															{loanDefinition.name}
														</TextMantine>
													</TooltipMantine>
												</Stack>
											}
											sectionSeparator={isFirst}
											sectionLabel={
												isFirst
													? tMipManage('new_joiner.steps.investors.loans')
													: undefined
											}
											childrenAlign="center"
											sectionIcon="bank"
										>
											{tMipManage('new_joiner.steps.investors.amount')}
										</HeaderCell>
									</React.Fragment>
								);
							})
						: null}

					{showVesting && (
						<HeaderCell
							size={3}
							groupAlign="center"
							childrenAlign="center"
							sectionSeparator={true}
							sectionIcon="paper"
							sectionLabel="Vesting terms"
							key="vesting"
							className="vesting-header-cell"
						>
							<Flex
								direction="column"
								w="100%"
								mr="0.65rem"
								mt="0.25rem"
								gap="0.5rem"
							>
								{vestingBulkUpdateMode ? (
									<>
										{makeVestingTermDropdown(
											bulkVestingTerm,
											onBulkVestingTermChange
										)}
										<Flex justify="center">
											<TableCheckbox
												onClick={() => {
													selectAllInvestors();
												}}
												checked={isAllInvestorsSelected()}
												label={tCommonUi('common_buttons.select_all')}
											/>
										</Flex>
									</>
								) : (
									<Button
										variant="outline"
										size="compact-sm"
										disabled={!vestingTerms?.length || !investorsList?.length}
										sx={(theme) => ({
											border: `1px solid ${theme.colors.gray[4]}`,
											color: theme.colors.dark[9],
											justifyContent: 'flex-start',
											display: 'flex',
											overflow: 'hidden',
											svg: {
												color: theme.colors.blue[6],
											},
										})}
										leftSection={
											<Icon
												variant="bulls-eye"
												size="vlg"
												style={{ marginRight: '0.25rem' }}
											/>
										}
										onClick={() =>
											onBulkUpdateModeChange(!vestingBulkUpdateMode)
										}
									>
										{tCommonUi('common_buttons.bulk_update')}
									</Button>
								)}
							</Flex>
						</HeaderCell>
					)}

					{mainContracts?.map((contract, index) => {
						const isFirst = index === 0;
						const totalAssigned = investorsList?.filter((investor) =>
							investor.contracts?.find(
								(investorContractId) => investorContractId === contract.id
							)
						)?.length;

						return (
							<HeaderCell
								size={2}
								groupAlign="center"
								childrenAlign="center"
								sectionSeparator={isFirst}
								sectionIcon="paper"
								groupComponent={
									totalAssigned ? (
										<TableHeaderContractCellGroup>
											<ChipsV2
												size="s"
												color={COLOR.GREEN}
												chipTheme={CHIP_THEME.LIGHT}
												prefixIcon="checkmark-circle-contained-filled"
											>
												{totalAssigned || undefined}
											</ChipsV2>
										</TableHeaderContractCellGroup>
									) : (
										<div />
									)
								}
								sectionLabel={
									isFirst
										? tMipCreate('investors.table.header.contracts_documents')
										: undefined
								}
								key={contract.id}
							>
								{investorsList?.length ? (
									<TableHeaderContractCellGroup>
										<UIElementIdentifier
											identifier={
												TEST_ELEMENT_IDS.MIP_CREATE_INVESTORS_SELECT_ALL_CONTRACTS
											}
											index={index}
										>
											<TableCheckbox
												onClick={() => {
													onSelectAllClicked(contract);
												}}
												checked={
													contractsAllSelection.find(
														(itemContract) =>
															itemContract.contractID === contract.id
													)?.isChecked ?? false
												}
											/>
										</UIElementIdentifier>
										<TextWithTooltip
											mainText={contract.name}
											tooltipText={contract.name}
											textProps={{
												colorFaded: true,
												variant: 'overline',
												uppercase: true,
											}}
										/>
									</TableHeaderContractCellGroup>
								) : (
									<TextWithTooltip
										mainText={contract.name}
										tooltipText={contract.name}
										textProps={{
											colorFaded: true,
											variant: 'overline',
											uppercase: true,
										}}
									/>
								)}
							</HeaderCell>
						);
					})}

					{!!sideAgreements?.length && (
						<HeaderCell
							customSize="minmax(10rem, 1fr)"
							groupAlign="right"
							childrenAlign="right"
							groupComponent={
								totalSideAgreementsAssigned ? (
									<div>
										<ChipsV2
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
											prefixIcon="checkmark-circle-contained-filled"
										>
											{totalSideAgreementsAssigned || undefined}
										</ChipsV2>
									</div>
								) : null
							}
						>
							{tMipCreate('investors.table.header.side_agreements')}
						</HeaderCell>
					)}
				</HeaderRow>
				<BodyRow>
					{investorsList?.map((investor, index) => {
						return (
							<Row
								data-testid={TEST_ELEMENT_IDS.MIP_CREATE_INVESTORS_UPLOADED_ENTRY}
								key={`${investor.email}${index}`}
							>
								<TextCell text={investor.firstName} elipsis />
								<TextCell text={investor.lastName} elipsis />
								<TextCell text={investor.email} elipsis />

								{securitiesList?.map((security) => {
									const investorSecurityData = investor.securities?.find(
										(investorSecurity) =>
											investorSecurity.type === security.type &&
											investorSecurity.name === security.name
									);

									return (
										<React.Fragment key={security?.id}>
											<Cell
												align="center"
												isInvalid={!investorSecurityData?.totalNumber}
											>
												<Text variant="p-sm">
													<NumberFormatted
														value={investorSecurityData?.totalNumber}
													/>
												</Text>
											</Cell>
											<Cell
												align={
													!investorSecurityData?.totalCost
														? 'center'
														: 'right'
												}
												isInvalid={!investorSecurityData?.totalCost}
											>
												<Text variant="p-sm">
													<AmountFormatted
														value={investorSecurityData?.totalCost}
														currency={currency}
													/>
												</Text>
											</Cell>
										</React.Fragment>
									);
								})}

								{loansDefinitionsList?.map((loanDefinition) => {
									const investorLoanData = investor.loans?.find(
										(investorLoan) =>
											investorLoan.loanDefinitionId === loanDefinition.id
									);

									return (
										<React.Fragment key={loanDefinition?.id}>
											<Cell
												align={
													investorLoanData?.amount ? 'right' : 'center'
												}
											>
												<Text variant="p-sm">
													<AmountFormatted
														value={investorLoanData?.amount}
														currency={currency}
													/>
												</Text>
											</Cell>
										</React.Fragment>
									);
								})}

								{showVesting && (
									<Cell>
										{vestingTerms?.length ? (
											<Flex w="100%" justify="center">
												{vestingBulkUpdateMode ? (
													<TableCheckbox
														onClick={() => {
															onInvestorCheckboxClick(investor.email);
														}}
														checked={
															!!selectedInvestors.find(
																(email) => investor.email === email
															)
														}
													/>
												) : (
													makeVestingTermDropdown(
														investorVestingTerms.get(investor.email) ??
															'',
														(value) =>
															onInvestorDropdownChange(
																investor.email,
																value || ''
															)
													)
												)}
											</Flex>
										) : (
											<Text variant="p-sm">{LONG_DASH}</Text>
										)}
									</Cell>
								)}

								{mainContracts?.map((contract) => {
									const isChecked = !!investor.contracts?.find(
										(investorContractId) => investorContractId === contract.id
									);

									const onClick = () => {
										const updatedContracts: string[] = isChecked
											? investor.contracts?.filter(
													(investorContractId) =>
														investorContractId !== contract?.id
												) ?? []
											: [...(investor.contracts || []), contract.id];
										onUpdateInvestorContracts(updatedContracts, investor.id);
									};

									return (
										<Cell align="left" key={contract.id}>
											<UIElementIdentifier
												identifier={
													TEST_ELEMENT_IDS.MIP_CREATE_INVESTORS_SELECT_CONTRACT
												}
												index={index}
											>
												<TableCheckbox
													onClick={onClick}
													checked={isChecked}
												/>
											</UIElementIdentifier>
										</Cell>
									);
								})}

								{!!sideAgreements?.length && (
									<Cell align="right">
										<SideAgreementsPopup
											onUpdateInvestorContracts={onUpdateInvestorContracts}
											sideAgreements={sideAgreements}
											investor={investor}
											index={index}
										/>
									</Cell>
								)}
							</Row>
						);
					})}
				</BodyRow>
			</DefaultTable>
		</div>
	);
};

const TextCell = ({
	text,
	align,
	...props
}: { text: string | number; align?: Align } & ITextProps) => {
	const isInvalid = useMemo(() => {
		return isInvalidTableTextCell(text);
	}, [text]);

	return (
		<Cell align={align} isInvalid={isInvalid}>
			<Text {...props} variant="p-sm">
				{text}
			</Text>
		</Cell>
	);
};

const Styled = styled(NewJoinersAndAllocationsInvestorsTable)`
	${Cell}:not(:last-of-type) {
		padding-right: 1em;
	}

	${HeaderChildrenWrapper} {
		height: unset;
	}

	.vesting-header-cell {
		display: flex;
		height: 100%;
		flex-direction: column;
		justify-content: start;
	}

	${TableHeaderContractCellGroup} {
		${Tooltip} {
			margin-left: 0.5em;
		}
	}

	${HeaderRow} {
		${HeaderCell}:nth-child(1),
		${HeaderCell}:nth-child(2),
		${HeaderCell}:nth-child(3) {
			position: sticky;
			background: white;
			z-index: 10;
		}

		${HeaderCell}:nth-child(1) {
			left: 0.5em;

			&::after,
			&::before {
				content: '';
				position: absolute;
				top: 0;
				height: 100%;
				background: white;
				pointer-events: none;
				z-index: 11;
			}

			&::after {
				right: -1em;
				width: 1em;
			}

			&::before {
				left: -0.5em;
				width: 0.5em;
			}
		}

		${HeaderCell}:nth-child(2) {
			left: calc(2 * 4rem + 1em);
		}
		${HeaderCell}:nth-child(3) {
			left: calc(4 * 4rem + 1em);
		}

		${HeaderCell}:nth-child(4) {
			margin-left: 1em;
		}
	}

	${Row} {
		${Cell}:nth-child(1),
		${Cell}:nth-child(2),
		${Cell}:nth-child(3) {
			position: sticky;
			background: ${({ theme }) => theme.v2.color.neutral[100]};
			z-index: 9;
		}

		${Cell}:nth-child(1) {
			left: 0.5em;

			&::after,
			&::before {
				content: '';
				position: absolute;
				top: 0;
				height: 100%;
				background: ${({ theme }) => theme.v2.color.neutral[100]};
				pointer-events: none;
				z-index: 10;
			}

			&::after {
				right: -1em;
				width: 1em;
			}

			&::before {
				left: -0.5em;
				width: 0.5em;
			}
		}

		${Cell}:nth-child(2) {
			left: calc(2 * 4rem + 1em);
		}
		${Cell}:nth-child(3) {
			left: calc(4 * 4rem + 1em);
		}
	}
`;

export { Styled as NewJoinersAndAllocationsInvestorsTable };
